import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { API_URL, INTEREST_OPTIONS, ORG_TYPES } from "@/lib/constants";

const initial = {
  name: "",
  title: "",
  organization: "",
  email: "",
  phone: "",
  org_type: "",
  student_count: "",
  interest: "testfit_demo",
  timeline: "",
  message: "",
};

export default function Contact() {
  const [params] = useSearchParams();
  const preset = params.get("interest");
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (preset && INTEREST_OPTIONS.find((o) => o.value === preset)) {
      setForm((f) => ({ ...f, interest: preset }));
    }
  }, [preset]);

  const currentInterestLabel = useMemo(
    () => INTEREST_OPTIONS.find((o) => o.value === form.interest)?.label,
    [form.interest]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.interest) {
      toast.error("Please complete name, email and area of interest.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/inquiries`, form);
      toast.success("Thank you. Our team will be in touch within one business day.");
      setForm({ ...initial, interest: form.interest });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="overline text-[#0F4C5C]">Contact EdgeTech Global</div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.05] mt-4">
              Let's decide <em className="italic text-[#0F4C5C]">what's next</em> for your school.
            </h1>
          </div>
          <p className="md:col-span-4 text-[#22223B]/75 leading-relaxed">
            Tell us where you'd like to start — a TestFit® demonstration, a keynote, a workshop, or a custom system. We respond within one business day.
          </p>
        </div>
      </section>

      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-8">
            <div>
              <div className="overline">Where you'll reach us</div>
              <ul className="mt-6 space-y-4 text-[#22223B]/85">
                <li className="flex gap-3"><MapPin size={18} className="text-[#0F4C5C] mt-1" strokeWidth={1.5} /> Miami, FL</li>
                <li className="flex gap-3"><Phone size={18} className="text-[#0F4C5C] mt-1" strokeWidth={1.5} /> +1 740&nbsp;302&nbsp;3222</li>
                <li className="flex gap-3">
                  <Mail size={18} className="text-[#0F4C5C] mt-1" strokeWidth={1.5} />
                  <a href="mailto:admin@crystalgifford.com" className="hover:text-[#0F4C5C]">
                    admin@crystalgifford.com
                  </a>
                </li>
              </ul>
            </div>
            <div className="hairline" />
            <div>
              <div className="overline">Also visit</div>
              <div className="mt-4 space-y-3">
                <a href="https://testfit.org" target="_blank" rel="noreferrer" className="link-underline inline-flex items-center gap-1">
                  testfit.org <ArrowUpRight size={14} />
                </a>
                <div>
                  <a href="https://drcrystalgifford.com" target="_blank" rel="noreferrer" className="link-underline inline-flex items-center gap-1">
                    drcrystalgifford.com <ArrowUpRight size={14} />
                  </a>
                </div>
              </div>
            </div>
            <div className="hairline" />
            <div className="bg-[#F0EDE6] p-6 rounded-lg">
              <div className="overline text-[#0F4C5C]">You're inquiring about</div>
              <div className="font-serif-display text-xl mt-2">{currentInterestLabel}</div>
              <p className="text-sm text-[#22223B]/70 mt-3">
                Use the dropdown in the form to change this.
              </p>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-8 card-soft p-8 md:p-10"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Name*" name="name" value={form.name} onChange={handleChange} required testid="contact-name" />
              <Field label="Title" name="title" value={form.title} onChange={handleChange} testid="contact-title" />
              <Field label="Organization" name="organization" value={form.organization} onChange={handleChange} testid="contact-organization" />
              <Field label="Email*" name="email" type="email" value={form.email} onChange={handleChange} required testid="contact-email" />
              <Field label="Phone" name="phone" value={form.phone} onChange={handleChange} testid="contact-phone" />
              <SelectField
                label="Organization type"
                name="org_type"
                value={form.org_type}
                onChange={handleChange}
                options={ORG_TYPES.map((v) => ({ value: v, label: v }))}
                testid="contact-org-type"
              />
              <Field label="Number of students / participants" name="student_count" value={form.student_count} onChange={handleChange} testid="contact-student-count" />
              <Field label="Desired timeline" name="timeline" value={form.timeline} onChange={handleChange} placeholder="e.g., This semester, Fall 2026" testid="contact-timeline" />
              <div className="md:col-span-2">
                <SelectField
                  label="Area of interest*"
                  name="interest"
                  value={form.interest}
                  onChange={handleChange}
                  options={INTEREST_OPTIONS}
                  required
                  testid="contact-interest"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block">
                  <span className="text-sm font-medium text-[#22223B] mb-2 block">Additional details</span>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    data-testid="contact-message"
                    rows={5}
                    placeholder="Tell us about your school, timeline and any specific outcomes you're working toward."
                    className="w-full bg-[#FDFBF7] border border-black/10 rounded-md px-4 py-3 text-[#22223B] placeholder-[#22223B]/40 focus:outline-none focus:ring-1 focus:ring-[#0F4C5C]"
                  />
                </label>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3 items-center justify-between">
              <p className="text-xs text-[#6B705C]">We respond within one business day. Your information is used only to reply to this inquiry.</p>
              <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-primary">
                {loading ? "Sending..." : "Send Inquiry"} <ArrowUpRight size={16} />
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", placeholder, required, testid }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-[#22223B] mb-2 block">{label}</span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-[#FDFBF7] border border-black/10 rounded-md px-4 py-3 text-[#22223B] placeholder-[#22223B]/40 focus:outline-none focus:ring-1 focus:ring-[#0F4C5C]"
      />
    </label>
  );
}

function SelectField({ label, name, value, onChange, options, required, testid }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-[#22223B] mb-2 block">{label}</span>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-[#FDFBF7] border border-black/10 rounded-md px-4 py-3 text-[#22223B] focus:outline-none focus:ring-1 focus:ring-[#0F4C5C]"
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
